import type { MenuEntry } from '../ui/menu';

/** What the clipboard rows can do, and with what. */
export interface ClipboardOptions {
  /** Something is chosen that can be copied. */
  readonly chosen: boolean;
  /** Something chosen can also be taken off the page (it is not all locked). */
  readonly editable: boolean;
  /** Something chosen can be drawn to a picture: a layer, or one artboard. */
  readonly drawable: boolean;
  /** A viewer: copying is theirs, changing the page is not. */
  readonly readOnly?: boolean;
  /** Where the right-click was, in client pixels, for Paste here. Absent in the title menu. */
  readonly here?: { x: number; y: number };
  cut(): void;
  copy(): void;
  paste(at?: { x: number; y: number }): void;
  copyAs(format: 'png' | 'svg'): void;
  /** ⌥⌘C: the chosen layer's look, held to put on others. Absent where it is not offered. */
  copyProperties?(): void;
  /** ⌥⌘V: the held look on the chosen layers. */
  pasteProperties?(): void;
  /** A look has been copied, so Paste properties has something to paste. */
  readonly hasProperties?: boolean;
}

/**
 * Edit ▸ Cut, Copy, Paste, Paste here and Copy as ▸ PNG or SVG (W5-AR3, Farah's pick B: the
 * clipboard sits one level down, so the right-click menu stays short). What the selection
 * cannot do is left out, never greyed: nothing chosen leaves Paste alone. No marks on these
 * rows: the menu draws a mark on all of a group or on none, and Paste has none to wear.
 */
export function clipboardItems(o: ClipboardOptions): MenuEntry[] {
  const items: MenuEntry[] = [];
  if (o.chosen && o.editable && !o.readOnly) items.push({ id: 'cut', label: 'Cut', shortcut: '⌘X', onSelect: o.cut });
  if (o.chosen) items.push({ id: 'copy', label: 'Copy', shortcut: '⌘C', onSelect: o.copy });
  if (!o.readOnly) items.push({ id: 'paste', label: 'Paste', shortcut: '⌘V', onSelect: () => o.paste() });
  const here = o.here;
  if (here && !o.readOnly) items.push({ id: 'paste-here', label: 'Paste here', onSelect: () => o.paste(here) });
  if (o.chosen && o.drawable) {
    items.push({
      kind: 'submenu', id: 'copy-as', label: 'Copy as', items: [
        { id: 'copy-png', label: 'PNG', onSelect: () => o.copyAs('png') },
        { id: 'copy-svg', label: 'SVG', onSelect: () => o.copyAs('svg') },
      ],
    });
  }
  // Copy and Paste properties (⌥⌘C, ⌥⌘V), Figma's pair: a layer's look, not the layer.
  const looks: MenuEntry[] = [];
  if (o.chosen && o.copyProperties) looks.push({ id: 'copy-properties', label: 'Copy properties', shortcut: '⌥⌘C', onSelect: o.copyProperties });
  if (o.chosen && o.editable && !o.readOnly && o.hasProperties && o.pasteProperties) looks.push({ id: 'paste-properties', label: 'Paste properties', shortcut: '⌥⌘V', onSelect: o.pasteProperties });
  if (looks.length > 0) items.push(...(items.length > 0 ? [{ kind: 'separator' as const }] : []), ...looks);
  return items;
}

/** A submenu called Edit holding the clipboard rows: what the right-click menu gains. */
export function clipboardSubmenu(o: ClipboardOptions): MenuEntry {
  return { kind: 'submenu', id: 'clipboard', label: 'Edit', icon: 'copy', items: clipboardItems(o) };
}
